import { Controller } from '@hotwired/stimulus';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const roundToStep = (value, step) =>
  Number(value.toFixed((String(step).split('.')[1] || '').length));

// Keeps the filled part of the track, the steppers' bounds and the spoken value of a range input
// in step with it. `valuetext` is the spoken text with `%{value}` standing for the number, so a
// screen reader hears the current value rather than the one the page started with.
export default class extends Controller {
  static targets = ['input', 'stepDown', 'stepUp'];

  static values = {
    min: { type: Number, default: 0 },
    max: { type: Number, default: 100 },
    step: { type: Number, default: 1 },
    valuetext: String
  };

  connect() {
    this.refresh();
  }

  // ── Public actions (wired from data-action) ────────────────────────────────
  handleInput() {
    this.commit(Number(this.input.value));
  }

  // A stepper at its bound is only marked unavailable, so it still takes the click.
  stepDown() {
    if (this.currentValue <= this.minValue) return;

    this.commit(this.currentValue - this.stepValue);
  }

  stepUp() {
    if (this.currentValue >= this.maxValue) return;

    this.commit(this.currentValue + this.stepValue);
  }

  // ── Internals ──────────────────────────────────────────────────────────────
  commit(next) {
    const clamped = clamp(roundToStep(next, this.stepValue), this.minValue, this.maxValue);
    this.input.value = String(clamped);
    this.refresh();
    this.dispatch('change', { detail: { value: clamped } });
  }

  // The CSS reads the fill from `--ui-slider-fill`.
  refresh() {
    const value = this.currentValue;
    const range = this.maxValue - this.minValue;
    const fill = range === 0 ? 0 : ((value - this.minValue) / range) * 100;

    this.input.style.setProperty('--ui-slider-fill', `${fill}%`);
    if (this.hasValuetextValue) {
      this.input.setAttribute('aria-valuetext', this.valuetextValue.replace('%{value}', value));
    }

    if (this.hasStepDownTarget) this.markStepper(this.stepDownTarget, value <= this.minValue);
    if (this.hasStepUpTarget) this.markStepper(this.stepUpTarget, value >= this.maxValue);
  }

  // A disabled slider disables its steppers for real; at a bound a stepper stays focusable,
  // so pressing it to the end does not drop the keyboard's place.
  markStepper(button, atBound) {
    button.disabled = this.input.disabled;

    if (atBound && !this.input.disabled) {
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-disabled');
    }
  }

  // Without steppers the controller is attached to the input itself, so there
  // is no descendant target to look up.
  get input() {
    return this.hasInputTarget ? this.inputTarget : this.element;
  }

  get currentValue() {
    return Number(this.input.value);
  }
}
